import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Clipboard } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import type { VehicleTypeRow } from "@/lib/vehicleTypes";
import type { ShiftRow } from "./types";
import {
  buildAllocationMessage,
  computeAllocationCandidates,
  defaultSelectedJobIds,
  fetchJobsForDate,
  fetchPublishedBreaksForDate,
  formatDayLabel,
  groupShiftsByVehicleCategory,
  jobLine,
  type AllocationBreak,
  type AllocationCandidates,
  type AllocationMessageOptions,
  type CandidateJob,
  type TeamCandidates,
} from "./allocationMessage";

interface AllocationMessageDialogProps {
  /** null closes the dialog. */
  dateStr: string | null;
  onClose: () => void;
  allocationGroupsForDate: { schedule_group: string; shifts: ShiftRow[] }[];
  vehicleTypes: VehicleTypeRow[];
  options: AllocationMessageOptions;
}

const JobRow: React.FC<{ candidate: CandidateJob; checked: boolean; onToggle: (checked: boolean) => void }> = ({
  candidate,
  checked,
  onToggle,
}) => (
  <label className="flex items-center gap-2 text-xs text-gray-300 py-0.5 cursor-pointer hover:text-white">
    <Checkbox checked={checked} onCheckedChange={(c) => onToggle(!!c)} />
    <span>{jobLine(candidate.job)}</span>
  </label>
);

const TeamSection: React.FC<{
  title: string;
  team: TeamCandidates;
  selectedIds: Set<string>;
  onToggle: (jobId: string, checked: boolean) => void;
}> = ({ title, team, selectedIds, onToggle }) => {
  if (team.drivers.length === 0 && team.otherJobs.length === 0) return null;
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider">{title}</p>
      {team.drivers.map((d) => (
        <div key={d.shift.id}>
          <p className="text-[11px] font-semibold text-gray-400">{d.shift.driver_firstname || d.shift.driver_name}</p>
          {d.jobs.map((cj) => (
            <JobRow key={cj.job.id} candidate={cj} checked={selectedIds.has(cj.job.id)} onToggle={(c) => onToggle(cj.job.id, c)} />
          ))}
        </div>
      ))}
      {team.otherJobs.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-400">Other Jobs</p>
          {team.otherJobs.map((cj) => (
            <JobRow key={cj.job.id} candidate={cj} checked={selectedIds.has(cj.job.id)} onToggle={(c) => onToggle(cj.job.id, c)} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Review step for the night-before message: fetches the day's jobs once,
 * splits them into candidate sections (see computeAllocationCandidates),
 * and lets the dispatcher tick which ones actually go out -- the preview
 * text on the right regenerates live from the current selection.
 */
const AllocationMessageDialog: React.FC<AllocationMessageDialogProps> = ({
  dateStr,
  onClose,
  allocationGroupsForDate,
  vehicleTypes,
  options,
}) => {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<AllocationCandidates | null>(null);
  const [breaks, setBreaks] = useState<AllocationBreak[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const allShiftsToday = useMemo(() => allocationGroupsForDate.flatMap((g) => g.shifts), [allocationGroupsForDate]);
  // Regrouped by what's actually being driven that day (real vehicle type /
  // STS shift code), not by the driver's nominal schedule_group -- see
  // groupShiftsByVehicleCategory for why.
  const vehicleCategoryGroups = useMemo(() => groupShiftsByVehicleCategory(allShiftsToday, vehicleTypes), [allShiftsToday, vehicleTypes]);

  useEffect(() => {
    if (!dateStr) {
      setCandidates(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setCandidates(null);
    Promise.all([fetchJobsForDate(dateStr), fetchPublishedBreaksForDate(dateStr)])
      .then(([jobs, brks]) => {
        if (cancelled) return;
        const c = computeAllocationCandidates(allShiftsToday, jobs, vehicleTypes);
        setCandidates(c);
        setSelectedIds(defaultSelectedJobIds(c));
        setBreaks(brks);
      })
      .catch((err) => {
        if (cancelled) return;
        toast({ title: "Error", description: err instanceof Error ? err.message : "Failed to load jobs", variant: "destructive" });
        onClose();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  const previewText = useMemo(() => {
    if (!dateStr || !candidates) return "";
    return buildAllocationMessage({
      dayLabel: formatDayLabel(dateStr),
      vehicleCategoryGroups,
      breaks,
      candidates,
      selectedJobIds: selectedIds,
      options,
    });
  }, [dateStr, candidates, breaks, selectedIds, options, vehicleCategoryGroups]);

  const toggle = (jobId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(jobId);
      else next.delete(jobId);
      return next;
    });
  };

  const hasAnyCandidates =
    !!candidates &&
    (candidates.sedanSuv.length > 0 ||
      candidates.tmv.drivers.length > 0 ||
      candidates.tmv.otherJobs.length > 0 ||
      candidates.sts.drivers.length > 0 ||
      candidates.sts.otherJobs.length > 0);

  return (
    <Dialog open={!!dateStr} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl bg-gray-900 border-gray-700 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            {dateStr ? `Message for ${format(new Date(`${dateStr}T00:00:00`), "EEEE d MMM")}` : "Message"}
          </DialogTitle>
        </DialogHeader>

        {loading && <p className="text-sm text-gray-400">Loading jobs...</p>}

        {!loading && candidates && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-2">
              {options.includeSedanSuv && candidates.sedanSuv.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-1">Early Morning Jobs (before 12 noon)</p>
                  {candidates.sedanSuv.map((cj) => (
                    <JobRow key={cj.job.id} candidate={cj} checked={selectedIds.has(cj.job.id)} onToggle={(c) => toggle(cj.job.id, c)} />
                  ))}
                </div>
              )}

              {options.includeTmv && <TeamSection title="TMV's" team={candidates.tmv} selectedIds={selectedIds} onToggle={toggle} />}
              {options.includeSts && <TeamSection title="STS's" team={candidates.sts} selectedIds={selectedIds} onToggle={toggle} />}

              {!hasAnyCandidates && <p className="text-xs text-gray-500">No jobs found for this day.</p>}
            </div>

            <Textarea
              value={previewText}
              readOnly
              className="min-h-[400px] bg-gray-800 border-gray-600 text-gray-200 font-mono text-xs whitespace-pre-wrap"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!candidates}
            onClick={() => {
              navigator.clipboard.writeText(previewText).then(() => {
                toast({ title: "Copied", description: "Message copied to clipboard" });
              });
            }}
          >
            <Clipboard className="h-3.5 w-3.5 mr-1.5" /> Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AllocationMessageDialog;
